import { useCallback, useEffect, useRef, useState } from 'react';

import { NetworkError } from '@/lib/api';
import { useApi } from '@/lib/api-context';

import { trackResponseSchema } from './types';
import { MAX_MARKS, useOfflineMarks } from './use-offline-marks';

export { MAX_MARKS };

// Latidos de trabajo del Editor (so=420, spec §4.1, D3-D4). Un latido sale solo con
// interacción real en la ventana visible, como máximo uno por intervalo, y nunca con el
// hook deshabilitado (en pausa). Si la red falla, el instante queda en la cola de marcas
// sin conexión (use-offline-marks).

const BASE = '/api/v1/quality/time-engine';
const EVENTS = ['keydown', 'pointerdown', 'wheel', 'touchstart'] as const;
/** Espera máxima del latido en vuelo antes de pausar: un pedido colgado no bloquea Pausar. */
export const SETTLE_TIMEOUT_MS = 10_000;

export interface HeartbeatOptions {
  flowInstanceId: string;
  intervalSeconds: number;
  /** Etapa de trabajo abierta, con permiso y sin pausa. */
  enabled: boolean;
  onStageClosed: () => void;
  onBeat?: () => void;
  /** Tope de marcas sin conexión (por defecto el del servidor). */
  maxMarks?: number;
}

export interface HeartbeatControl {
  /** Instante (ms) del último latido aceptado en esta sesión de la pantalla. */
  lastBeatAt: number | null;
  pendingMarks: number;
  /** Bloquea nuevos latidos y espera el que está en vuelo: se llama antes de pausar. */
  settle: () => Promise<void>;
}

/** Solo una petición que no llegó al servidor se guarda como marca sin conexión: otra falla
 *  (respuesta del API, error de programación) se reenviaría y contaría tiempo de más. */
const isNetworkFailure = (e: unknown): boolean => e instanceof NetworkError;

export function useWorkHeartbeat(opts: HeartbeatOptions): HeartbeatControl {
  const api = useApi();
  const [lastBeatAt, setLastBeatAt] = useState<number | null>(null);
  const marks = useOfflineMarks(opts.flowInstanceId, opts.maxMarks);
  const { add: addMark, flush: flushMarks } = marks;
  const optsRef = useRef(opts);
  const lastSentRef = useRef<number | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const heldRef = useRef(false);
  const closedRef = useRef(false);

  useEffect(() => {
    optsRef.current = opts;
  });

  const send = useCallback(async (): Promise<void> => {
    const now = Date.now();
    lastSentRef.current = now;
    try {
      const raw = await api.post(`${BASE}/heartbeat`, {
        flowInstanceId: optsRef.current.flowInstanceId,
      });
      const parsed = trackResponseSchema.safeParse(raw);
      if (!parsed.success) {
        return;
      }
      if (!parsed.data.data.tracking) {
        closedRef.current = true;
        optsRef.current.onStageClosed();
        return;
      }
      setLastBeatAt(now);
      optsRef.current.onBeat?.();
      await flushMarks();
    } catch (e) {
      if (isNetworkFailure(e)) {
        addMark(new Date(now));
      }
    }
  }, [api, flushMarks, addMark]);

  /** Salir del documento (so=420): el servidor cierra el tramo en el último latido. Va
   *  después del latido en vuelo para que lleguen en orden; al volver se late de inmediato. */
  const leave = useCallback((): void => {
    const flowInstanceId = optsRef.current.flowInstanceId;
    lastSentRef.current = null;
    void (inFlightRef.current ?? Promise.resolve())
      .then(() => api.post(`${BASE}/leave`, { flowInstanceId }))
      .catch(() => undefined);
  }, [api]);

  // Desmontar el Editor con la sesión activa es salir del documento. Deshabilitarse (una
  // pausa) no lo es: la pausa cierra el tramo en su propio instante.
  useEffect(
    () => () => {
      if (optsRef.current.enabled) {
        leave();
      }
    },
    [leave],
  );

  // Reanudar (enabled pasa a true) libera el bloqueo de la pausa.
  useEffect(() => {
    if (opts.enabled) {
      heldRef.current = false;
    }
  }, [opts.enabled]);

  // Un flujo nuevo empieza sin cierre ni cadencia heredados.
  useEffect(() => {
    closedRef.current = false;
    lastSentRef.current = null;
  }, [opts.flowInstanceId]);

  useEffect(() => {
    if (!opts.enabled) {
      return undefined;
    }
    const onInteraction = (): void => {
      if (heldRef.current || closedRef.current || inFlightRef.current !== null) {
        return;
      }
      if (document.visibilityState !== 'visible') {
        return;
      }
      const last = lastSentRef.current;
      if (last !== null && Date.now() - last < optsRef.current.intervalSeconds * 1000) {
        return;
      }
      const p = send().finally(() => {
        inFlightRef.current = null;
      });
      inFlightRef.current = p;
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        leave();
      }
    };
    for (const ev of EVENTS) {
      window.addEventListener(ev, onInteraction, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      for (const ev of EVENTS) {
        window.removeEventListener(ev, onInteraction);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [opts.enabled, send, leave]);

  const settle = useCallback(async (): Promise<void> => {
    heldRef.current = true;
    const inFlight = inFlightRef.current;
    if (inFlight === null) {
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, SETTLE_TIMEOUT_MS);
    });
    await Promise.race([inFlight, timeout]);
    clearTimeout(timer);
  }, []);

  return { lastBeatAt, pendingMarks: marks.pendingMarks, settle };
}
